import { loadBrandLogoDataUrl, loadSavedCrestDataUrl } from './letterAssets';

// ── Canva slide helpers ─────────────────────────────────────────────────────
// Shared look for the 1920×1080 PNG slides staff drop into the Canva welcome
// presentation (Team Categories, Rooming List): navy background, big title,
// brand logo top-left and club crest top-right.

export const SLIDE_W = 1920;
export const SLIDE_H = 1080;

export const NAVY  = '#0D2240';
export const NAVY2 = '#16365F';
export const AMBER = '#E8A020';
export const INK   = '#111827';
export const MUTED = '#6B7280';
export const FONT  = "'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif";

// Top of the content area below the title block.
export const CONTENT_TOP = 248;

export function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Shrinks the font until `text` fits `maxW`.
export function fitText(ctx, text, maxW, weight, size, min = 12) {
  let s = size;
  ctx.font = `${weight} ${s}px ${FONT}`;
  while (s > min && ctx.measureText(text).width > maxW) {
    s -= 1;
    ctx.font = `${weight} ${s}px ${FONT}`;
  }
  return s;
}

export function ellipsize(ctx, text, maxW) {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
}

function drawBackground(ctx) {
  const bg = ctx.createLinearGradient(0, 0, SLIDE_W, SLIDE_H);
  bg.addColorStop(0, NAVY);
  bg.addColorStop(1, NAVY2);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
  // Faint pitch-circle detail in the corners
  ctx.strokeStyle = 'rgba(255,255,255,0.05)';
  ctx.lineWidth = 3;
  [[SLIDE_W + 40, -60, 420], [-120, SLIDE_H + 80, 360]].forEach(([x, y, r]) => {
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(x, y, r * 0.6, 0, Math.PI * 2); ctx.stroke();
  });
}

/**
 * Resizes `canvas` to 1920×1080 and paints the background, title, subtitle,
 * logo and crest. Returns the 2D context for the caller to draw the content.
 */
export async function startSlide(canvas, title, { subtitle = '', logoSrc = null, crestSrc = null } = {}) {
  canvas.width = SLIDE_W;
  canvas.height = SLIDE_H;
  const ctx = canvas.getContext('2d');
  try { await document.fonts?.load(`900 96px ${FONT}`); } catch (_) { /* fall back to system font */ }
  const [logo, crest] = await Promise.all([loadImage(logoSrc), loadImage(crestSrc)]);

  drawBackground(ctx);

  // Title
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `900 92px ${FONT}`;
  if ('letterSpacing' in ctx) ctx.letterSpacing = '6px';
  ctx.fillText(title, SLIDE_W / 2, 132);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
  ctx.fillStyle = AMBER;
  ctx.fillRect(SLIDE_W / 2 - 70, 156, 140, 6);
  if (subtitle) {
    ctx.font = `700 26px ${FONT}`;
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fillText(ellipsize(ctx, subtitle.toUpperCase(), 1100), SLIDE_W / 2, 204);
  }

  // Brand logo top-left, club crest top-right on a white badge
  if (logo) {
    const lh = 84;
    const lw = Math.min(260, (logo.width / logo.height) * lh);
    ctx.drawImage(logo, 64, 56, lw, (logo.height / logo.width) * lw);
  }
  if (crest) {
    const r = 62;
    const cx = SLIDE_W - 64 - r;
    const cy = 56 + r;
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    const s = Math.min((r * 1.5) / crest.width, (r * 1.5) / crest.height);
    ctx.drawImage(crest, cx - (crest.width * s) / 2, cy - (crest.height * s) / 2, crest.width * s, crest.height * s);
  }
  return ctx;
}

/** Tour name for the subtitle plus the brand logo / club crest images. */
export async function loadSlideBranding(request) {
  const tourName = request?.tourName || (request?.schoolName ? `${request.schoolName} Tour` : '');
  const [logoSrc, crestSrc] = await Promise.all([
    loadBrandLogoDataUrl(request?.brand),
    loadSavedCrestDataUrl(request?.crestUrl),
  ]);
  return { tourName, logoSrc, crestSrc };
}

export function slideFileName(tourName, suffix) {
  return `${(tourName || 'Tour').replace(/[^a-z0-9]+/gi, '_')}_${suffix}.png`;
}

export async function downloadCanvasPng(canvas, fileName) {
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
